import type {
  TenantColors,
  TenantDarkTheme,
  TenantSeedTheme,
  TenantTheme
} from './types'

// HSL string `"H S% L%"` ↔ numeric helpers.

function parseHSL(value: string): { h: number, s: number, l: number } {
  const parts = value.replaceAll('%', '').split(/\s+/).map(Number)
  return { h: parts[0], s: parts[1], l: parts[2] }
}

function formatHSL(h: number, s: number, l: number): string {
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`
}

function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100
  const lNorm = l / 100
  const a = sNorm * Math.min(lNorm, 1 - lNorm)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const c = lNorm - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * c)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

/** Light theme derived from primary/secondary HSL. */
export function generateTheme(seed: TenantSeedTheme): TenantTheme {
  const p = parseHSL(seed.primary)
  return {
    primary: seed.primary,
    primaryForeground: '0 0% 100%',
    secondary: seed.secondary,
    secondaryForeground: '0 0% 100%',
    accent: seed.primary,
    accentForeground: '0 0% 100%',
    background: '0 0% 98%',
    foreground: formatHSL(p.h, 15, 20),
    card: '0 0% 100%',
    cardForeground: formatHSL(p.h, 15, 20),
    popover: '0 0% 100%',
    popoverForeground: formatHSL(p.h, 15, 20),
    muted: formatHSL(p.h, 15, 96),
    mutedForeground: formatHSL(p.h, 10, 50),
    border: formatHSL(p.h, 15, 90),
    input: formatHSL(p.h, 15, 94),
    ring: formatHSL(p.h, Math.round(p.s * 0.65), Math.round(p.l * 0.58)),
    destructive: '0 72% 51%',
    destructiveForeground: '0 0% 100%',
    gradientPrimaryFrom: seed.primary,
    gradientPrimaryTo: seed.secondary,
    sidebarBackground: formatHSL(p.h, 20, 98),
    sidebarForeground: formatHSL(p.h, 15, 25),
    sidebarPrimary: formatHSL(p.h, 85, 60),
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: formatHSL(p.h, 30, 94),
    sidebarAccentForeground: formatHSL(p.h, 15, 25),
    sidebarBorder: formatHSL(p.h, 20, 88),
    sidebarRing: formatHSL(p.h, 85, 60),
    emailPrimaryHex: seed.emailPrimaryHex,
    emailSecondaryHex: seed.emailSecondaryHex,
    emailBgHex: hslToHex(p.h, 15, 97),
    emailFooterBgHex: hslToHex(p.h, 20, 94)
  }
}

/** Dark theme derived from the same seed. */
export function generateDarkTheme(seed: TenantSeedTheme): TenantDarkTheme {
  const p = parseHSL(seed.primary)
  const fgHue = p.h - 225 < 0 ? p.h + 135 : p.h - 225
  return {
    background: formatHSL(p.h, 30, 8),
    foreground: formatHSL(fgHue, 60, 95),
    card: formatHSL(p.h, 25, 10),
    cardForeground: formatHSL(fgHue, 60, 95),
    popover: formatHSL(p.h, 25, 10),
    popoverForeground: formatHSL(fgHue, 60, 95),
    primary: seed.primary,
    primaryForeground: '0 0% 100%',
    secondary: seed.secondary,
    secondaryForeground: '0 0% 100%',
    muted: formatHSL(p.h, 20, 15),
    mutedForeground: formatHSL(p.h, 10, 65),
    accent: seed.primary,
    accentForeground: '0 0% 100%',
    destructive: '0 75% 60%',
    destructiveForeground: formatHSL(fgHue, 60, 95),
    border: formatHSL(p.h, 20, 18),
    input: formatHSL(p.h, 20, 18),
    ring: formatHSL(p.h, Math.round(p.s * 0.65), Math.round(p.l * 0.72)),
    sidebarBackground: formatHSL(p.h, 30, 8),
    sidebarForeground: formatHSL(fgHue, 60, 90),
    sidebarPrimary: formatHSL(p.h, 85, 70),
    sidebarPrimaryForeground: formatHSL(p.h, 30, 8),
    sidebarAccent: formatHSL(p.h, 25, 15),
    sidebarAccentForeground: formatHSL(fgHue, 60, 90),
    sidebarBorder: formatHSL(p.h, 20, 18),
    sidebarRing: formatHSL(p.h, 85, 70)
  }
}

/** Hex palette derived from primary/secondary hex. */
export function generateColors(
  primaryHex: string,
  secondaryHex: string
): TenantColors {
  return {
    textColorPrimary: primaryHex,
    textColorSecondary: darken(primaryHex, 30),
    textColorLighter: lighten(primaryHex, 30),
    textColorLighter2: lighten(primaryHex, 10),
    textColorLighter3: desaturate(lighten(primaryHex, 15), 20),
    textColorHeading: darken(primaryHex, 15),
    textColorContent: lighten(primaryHex, 20),
    textColorLight: '#464f60',
    textColorHelper: lighten(primaryHex, 5),
    textLightGrey: '#cbd1ea',
    textDarkGrey: '#a0aec3',
    textColorGreen: '#57e777',
    textColorGreen2: '#48a56c',
    textColorRed: '#aa5558',
    buttonColorPrimary: primaryHex,
    buttonColorPrimaryGradientStart: lighten(primaryHex, 8),
    buttonBackgroundColorPrimary: primaryHex,
    buttonBackgroundColorSubtle: '#f2f4f6',
    buttonBackgroundColorLighter: '#eef1f5',
    buttonColorSubtle: '#778ca2',
    buttonColorPrimaryDefault: '#eaedf0',
    buttonBorderColor: '#dde1eb',
    backgroundColorWhite: '#fff',
    // Subtle brand-tinted surfaces — derived from the primary so they track the
    // tenant hue (navy → cool blue-greys, gold → warm creams) instead of a
    // hardcoded peach that only suited the old orange brand.
    backgroundColorLighter: lighten(primaryHex, 87),
    backgroundColorLighter200: lighten(primaryHex, 84),
    backgroundColorDarker: lighten(primaryHex, 78),
    backgroundColorDarker2: '#cdd5e4',
    backgroundColorDarker3: '#e1e5ed',
    backgroundColorLighterRed: '#f9ecee',
    lightRedBackgroundColor: '#ffb4b4',
    darkRedTextColor: '#b22e2c',
    lightGreenBackgroundColor: '#96fab3',
    darkGreenTextColor: '#1d843c',
    dividerColor: lighten(primaryHex, 45),
    borderColor: '#eaecf0',
    borderColorDarker: `${primaryHex}3d`,
    sideNavShadowColor: '#e6eaef',
    toolbarBorderBottomColor: '#f7f7fa',
    dropdownDividerColor: '#f2f4f6'
  }
}

// --- hex helpers ---

function hexToRgb(hex: string): { r: number, g: number, b: number } {
  const c = hex.replace('#', '')
  return {
    r: Number.parseInt(c.substring(0, 2), 16),
    g: Number.parseInt(c.substring(2, 4), 16),
    b: Number.parseInt(c.substring(4, 6), 16)
  }
}

function rgbToHsl(
  r: number,
  g: number,
  b: number
): { h: number, s: number, l: number } {
  const rN = r / 255
  const gN = g / 255
  const bN = b / 255
  const max = Math.max(rN, gN, bN)
  const min = Math.min(rN, gN, bN)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === rN) h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6
    else if (max === gN) h = ((bN - rN) / d + 2) / 6
    else h = ((rN - gN) / d + 4) / 6
  }
  return { h: h * 360, s: s * 100, l: l * 100 }
}

function hslToRgb(
  h: number,
  s: number,
  l: number
): { r: number, g: number, b: number } {
  const sN = s / 100
  const lN = l / 100
  const a = sN * Math.min(lN, 1 - lN)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    return Math.round(255 * (lN - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)))
  }
  return { r: f(0), g: f(8), b: f(4) }
}

function rgbToHex(r: number, g: number, b: number): string {
  const t = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0')
  return `#${t(r)}${t(g)}${t(b)}`
}

export function lighten(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(r, g, b)
  const rgb = hslToRgb(h, s, Math.min(100, l + amount))
  return rgbToHex(rgb.r, rgb.g, rgb.b)
}

export function darken(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(r, g, b)
  const rgb = hslToRgb(h, s, Math.max(0, l - amount))
  return rgbToHex(rgb.r, rgb.g, rgb.b)
}

function desaturate(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(r, g, b)
  const rgb = hslToRgb(h, Math.max(0, s - amount), l)
  return rgbToHex(rgb.r, rgb.g, rgb.b)
}
